import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import { LocationSearch } from 'assets/templates/locationSearch/LocationSearch';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty, set, values } from 'lodash';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import Product from 'models/productManagement/product/Model.Product';
import providerDeleteLogoFileApi from 'models/productManagement/provider/deleteLogo/Api.Provider.DeleteLogo';
import providerSaveApi from 'models/productManagement/provider/save/Api.Provider.Save';
import providerUploadLogoFileApi from 'models/productManagement/provider/uploadLogo/Api.Provider.UploadLogo';
import { useProviderProvider } from '../Provider.Hooks';
import ProviderLogoTranslation from './edit/Provider.DocumentTranslations';

export type ProviderForm = Omit<Api.Core.Provider.Save.IRequest, 'logo'> & {
  logos: Array<
    Utils.Unpacked<Api.Core.Provider.Save.IRequest['logo']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
      translatedLogos: Array<
        Api.Core.DocumentTranslation.Save.IRequest & {
          isDeleted?: boolean;
        }
      >;
    }
  >;
};

export default function ProviderEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, listApi } = useProviderProvider();
  const productApi = useApi({ action: Product.list });
  const listProviderLogoTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProviderLogo', documentId: [selected?.logo?.id] },
      wait: !selected?.logo?.id,
    },
    [selected?.logo?.id]
  );

  return (
    <ModalForm<ProviderForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected, listProviderLogoTranslationsApi]}
      title={lang.provider}
      default={{
        ...selected?.toJSON(),
        logos: selected?.logo
          ? [selected?.logo]?.map((it) => ({
              ...it,
              translatedLogos: listProviderLogoTranslationsApi.payload?.data.filter((t) => t.documentId === it.id),
            }))
          : [],
      }}
      onCancel={pageRouter.updateParams}
      onSubmit={async ({ logos, ...data }) => {
        const response = await providerSaveApi(data);
        if (response.status === HttpStatus.ok) {
          const providerId = response.payload || data.id;
          let totalFiles = 0;
          let uploadedFiles = 0;
          let deletedFiles = 0;
          let totalDeletedFiles = 0;

          for (const logo of logos || []) {
            let logoId: null | number = logo?.id;
            if (logo?.isDeleted && logo?.id) {
              const docDeleteReponse = await providerDeleteLogoFileApi(providerId, { logoId });
              if (docDeleteReponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            } else if (logo?.formFile) {
              const docUploadResponse = await providerUploadLogoFileApi(providerId, {
                formFile: logo?.formFile?.original,
                languageIsoCode: Language.english,
              });
              logoId = docUploadResponse?.payload;
              if (docUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }

            for (const translatedLogo of logo?.translatedLogos || []) {
              if ((logo?.isDeleted || translatedLogo?.isDeleted) && translatedLogo?.id) {
                const docDeleteReponse = await documentTranslationDeleteApi({
                  id: translatedLogo?.id,
                });
                if (docDeleteReponse.status === HttpStatus.ok) {
                  deletedFiles += 1;
                }
                totalDeletedFiles += 1;
              } else if (translatedLogo.formFile && !translatedLogo.id) {
                const docUploadResponse = await documentTranslationSaveApi({ ...translatedLogo, documentId: logoId });
                if (docUploadResponse.status === HttpStatus.ok) {
                  uploadedFiles += 1;
                }
                totalFiles += 1;
              }
            }
          }
          if (uploadedFiles !== totalFiles || deletedFiles !== totalDeletedFiles) {
            addLog({
              warning: combineStrings(
                ' ',
                lang.formSuccessAndFilesFailedMessage,
                totalFiles > 0 ? `${uploadedFiles}/${totalFiles} ${lang.filesUploaded}` : null,
                totalDeletedFiles > 0 ? `${deletedFiles}/${totalDeletedFiles} ${lang.filesGotDeleted}!` : null
              ),
            });
          } else {
            addLog({ success: lang.saveSuccess });
          }
        }

        if (response.status === HttpStatus.internalServerError) addLog({ error: response.message });
        listApi.execute((body) => body);
        pageRouter.updateParams();
      }}
      validation={(data, errors) => {
        if (!data.name) set(errors, 'name', lang.mustNotBeEmpty);
        if (!data.streetAddress) set(errors, 'streetAddress', lang.mustNotBeEmpty);
        if (!data.city) set(errors, 'city', lang.mustNotBeEmpty);
        if (!data.provinceCode) set(errors, 'provinceCode', lang.mustNotBeEmpty);
        if (!data.countryCode) set(errors, 'countryCode', lang.mustNotBeEmpty);
        if (!data.postalCode) set(errors, 'postalCode', lang.mustNotBeEmpty);
        if (!data.products || isEmpty(data.products)) set(errors, 'products._objectError', lang.mustNotBeEmpty);

        const activeLogos = (data.logos ?? []).filter((logo) => !logo.isDeleted);
        const hasFile = (item: any) => item?.mediaFile || item?.formFile;
        const logoExists = activeLogos.some((logo) => hasFile(logo));
        const translationLogoExists = activeLogos.some((logo) =>
          (logo.translatedLogos ?? []).some((it) => hasFile(it) && !it.isDeleted)
        );
        if (!logoExists) {
          set(errors, 'logos._objectError', lang.requiredLogo);
        }
        if (logoExists && !translationLogoExists) {
          set(errors, 'logos._objectError', lang.requiredTranslation);
        }
      }}
      render={({ form }) => {
        const activeLogos = (form.data?.logos ?? []).filter((logo) => !logo.isDeleted);
        const hasFile = (item: any) => item?.mediaFile || item?.formFile;
        const logoExists = activeLogos.some((logo) => hasFile(logo));
        const translationLogoExists = activeLogos.some((logo) =>
          (logo.translatedLogos ?? []).some((it) => hasFile(it) && !it.isDeleted)
        );

        return (
          <>
            <FormWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={form.data.name}
                error={form.errors.name}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <LocationSearch
                onSelect={(location) => {
                  form.update({
                    streetAddress: location?.Line1 || null,
                    provinceCode: location?.ProvinceCode || null,
                    city: location?.City || null,
                    countryCode: location?.CountryIso3 || null,
                    postalCode: location?.PostalCode || null,
                  });
                }}
              />
              <InputWrapper>
                <TextInput
                  name="streetAddress"
                  label={lang.streetAddress}
                  value={form.data.streetAddress}
                  error={form.errors.streetAddress}
                  onChange={form.update}
                  icon={<Icon class="if-map-pin" />}
                />
                <TextInput
                  name="postalCode"
                  label={lang.postalCode}
                  value={form.data.postalCode}
                  error={form.errors.postalCode}
                  onChange={form.update}
                  icon={<Icon class="if-map-pin" />}
                />
                <TextInput
                  name="city"
                  label={lang.city}
                  value={form.data.city}
                  error={form.errors.city}
                  onChange={form.update}
                  icon={<Icon class="if-map-pin" />}
                />
                <TextInput
                  name="provinceCode"
                  label={lang.provinceCode}
                  value={form.data.provinceCode}
                  error={form.errors.provinceCode}
                  onChange={form.update}
                  icon={<Icon class="if-map-pin" />}
                  style={{ width: Theme.Size.XXL }}
                />
                <TextInput
                  name="countryCode"
                  label={lang.countryCode}
                  value={form.data.countryCode}
                  error={form.errors.countryCode}
                  onChange={form.update}
                  icon={<Icon class="if-map-pin" />}
                  style={{ width: Theme.Size.XXL }}
                />
              </InputWrapper>
              <SelectInput
                name="products"
                label={lang.products}
                data={productApi?.payload?.data?.map((item) => item.displayInfo)}
                isMultiselect
                value={form.data.products?.map((it) => it.id)}
                error={form.errors?.products?._objectError}
                onChange={({ products: idList }) => form.update({ products: idList.map((id) => ({ id })) })}
                icon={<Icon class="fas-clipboard-list" />}
              />
              <InputWrapper style={{ marginBottom: Theme.Size.L }}>
                <FileInputSimple
                  name="files"
                  label={lang.logos}
                  fileLimit={1}
                  allowedMimeTypes={[MimeType.allImageFiles]}
                  error={form.errors?.logos?._objectError}
                  onChange={({ files }) => {
                    form.update({
                      logos: [
                        ...(form.data.logos ?? []),
                        ...files.map((file) => ({
                          formFile: file,
                          translatedLogos: [],
                        })),
                      ],
                    });
                  }}
                />
              </InputWrapper>
              {(!logoExists || !translationLogoExists) && (
                <p
                  style={{
                    backgroundColor: Theme.Color.error,
                    color: 'white',
                    padding: `${Theme.Size.M} ${Theme.Size.L}`,
                    fontWeight: 'bold',
                    marginBottom: Theme.Size.XS,
                  }}
                >
                  <ul>
                    {!logoExists && <li>{lang.requiredLogo}</li>}
                    {!translationLogoExists && <li>{lang.requiredTranslation}</li>}
                  </ul>
                </p>
              )}
              <table className={ThemeTableSimple}>
                <thead>
                  <tr>
                    <th>{lang.fileName}</th>
                    <DataMap
                      data={values(Language)}
                      render={({ data: langIsoCode }) => <th key={langIsoCode}>{lang[langIsoCode]}</th>}
                    />
                  </tr>
                </thead>
                <tbody>
                  <DataMap
                    data={form.data.logos ?? []}
                    render={({ data: logo, index }) =>
                      !logo.isDeleted && (
                        <tr key={`${logo.id ?? 0}-${index}`}>
                          <td>{logo.mediaFile?.name ?? logo.formFile?.original?.name ?? 'N/A'}</td>
                          <DataMap
                            data={values(Language)}
                            render={({ data: languageIsoCode }) => (
                              <td key={languageIsoCode}>
                                <ProviderLogoTranslation languageIsoCode={languageIsoCode} logo={logo} index={index} />
                              </td>
                            )}
                          />
                        </tr>
                      )
                    }
                  />
                </tbody>
              </table>
            </FormWrapper>
          </>
        );
      }}
    />
  );
}
